import { Config } from "@app/config";
import { faMoneyBill1 as farMoneyBill1 } from "@awesome.me/kit-3777c2145e/icons/classic/regular";
import { faEarthAmericas as fasEarthAmericas } from "@awesome.me/kit-3777c2145e/icons/classic/solid";
import type { AbstractSubmitStepProps } from "@component/assets/AbstractLeadForm/Steps/AbstractSubmitStep";
import { AbstractSubmitStep } from "@component/assets/AbstractLeadForm/Steps/AbstractSubmitStep";
import { INIT_VALUES } from "@component/RetailAccount/Calculator/domain";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { ProgressButtonState } from "@shared/ui";
import { FormCurrency, FormNumber, ProgressButton } from "@shared/ui";
import { formatText } from "@shared/ui/format";
import type { FormApi } from "final-form";
import type { ReactNode } from "react";
import { Card } from "react-bootstrap";

const MAX_AMOUNT = 3_000_000;
const MAX_COUNT = 50;

export interface StepFields {
	receiptOfMoneyAmount: number;
	internetTransferAmount: number;
	internetTransferCount: number;
	cashWithdrawalOwnAtmAmount: number;
	cashWithdrawalOwnAtmCount: number;
}

interface Props extends AbstractSubmitStepProps {
	variant?: string;
}

export class Step1 extends AbstractSubmitStep<StepFields, Props> {
	protected initValues(): StepFields {
		const { variant } = this.props;
		let receiptOfMoneyAmount: number;
		let internetTransferAmount: number;

		switch (variant) {
			case "cib-eco-lead":
				receiptOfMoneyAmount = Config.getMinWage();
				break;
			case "cib-eco-plus":
				receiptOfMoneyAmount = 450_000;
				break;
			case "unicredit":
				receiptOfMoneyAmount = Config.getMinWage();
				break;
			case "raiffeisen":
				receiptOfMoneyAmount = 400_000;
				break;
			case "erste":
				receiptOfMoneyAmount = 225_000;
				break;
			case "compare-free-retail-account":
				receiptOfMoneyAmount = Config.getMinWage();
				internetTransferAmount = 100_000;
				break;
			default:
				receiptOfMoneyAmount = INIT_VALUES.receiptOfMoneyAmount;
				break;
		}

		return {
			receiptOfMoneyAmount,
			internetTransferAmount: internetTransferAmount ?? INIT_VALUES.internetTransferAmount,
			internetTransferCount: INIT_VALUES.internetTransferCount,
			cashWithdrawalOwnAtmAmount: INIT_VALUES.cashWithdrawalOwnAtmAmount,
			cashWithdrawalOwnAtmCount: INIT_VALUES.cashWithdrawalOwnAtmCount,
		};
	}

	protected renderCompareRetailAccountContent(
		form: FormApi<StepFields>,
		submitState: ProgressButtonState,
		disabled: boolean,
	): ReactNode {
		return (
			<>
				<Card.Body>
					<FormCurrency
						label="Havonta a számládra érkező összeg:"
						name="receiptOfMoneyAmount"
						validate={this.validReceiptOfMoneyAmount}
						disabled={disabled}
						required
					/>
					<FormCurrency
						prepend={<FontAwesomeIcon icon={fasEarthAmericas} />}
						label="Havi net/mobilbankos utalások összege:"
						name="internetTransferAmount"
						validate={this.validInternetTransferAmount}
						disabled={disabled}
						required
					/>
				</Card.Body>
				<Card.Footer className="text-center">
					<ProgressButton
						type="submit"
						size="lg"
						block
						className={!form.getState().valid ? "disabled" : ""}
						state={submitState}
					>
						Beküldöm
					</ProgressButton>
				</Card.Footer>
			</>
		);
	}

	protected renderContent(_values: StepFields, form: FormApi<StepFields>): ReactNode {
		const { submitState, variant } = this.props;
		const disabled = this.isDisabled();

		if (variant === "compare-free-retail-account") {
			return this.renderCompareRetailAccountContent(form, submitState, disabled);
		}

		return (
			<>
				<Card.Header>
					<Card.Title className="retail-text text-center">Használati jellemzők megadása</Card.Title>
				</Card.Header>
				<Card.Body>
					<FormCurrency
						label="Havonta a számládra érkező összeg:"
						name="receiptOfMoneyAmount"
						validate={this.validReceiptOfMoneyAmount}
						disabled={disabled}
						required
					/>
					<FormCurrency
						prepend={<FontAwesomeIcon icon={fasEarthAmericas} />}
						label="Havi net/mobilbankos utalások összege:"
						name="internetTransferAmount"
						validate={this.validInternetTransferAmount}
						disabled={disabled}
						required
					/>
					<FormNumber
						prepend={<FontAwesomeIcon icon={fasEarthAmericas} />}
						label="Havi net/mobilbankos utalások száma:"
						name="internetTransferCount"
						append="db"
						validate={this.validInternetTransferCount}
						disabled={disabled}
						required
					/>
					<FormCurrency
						prepend={<FontAwesomeIcon icon={farMoneyBill1} />}
						label="Havi készpénzfelvételek összege:"
						name="cashWithdrawalOwnAtmAmount"
						validate={this.validCashWithdrawalOwnAtmAmount}
						disabled={disabled}
						required
					/>
					<FormNumber
						prepend={<FontAwesomeIcon icon={farMoneyBill1} />}
						label="Havi készpénzfelvételek száma:"
						name="cashWithdrawalOwnAtmCount"
						append="db"
						validate={this.validCashWithdrawalOwnAtmCount}
						disabled={disabled}
						required
					/>
				</Card.Body>
				<Card.Footer className="text-center">
					<ProgressButton
						type="submit"
						size="lg"
						className={!form.getState().valid ? "disabled" : ""}
						state={submitState}
					>
						Mutasd a legjobb ajánlatokat
					</ProgressButton>
				</Card.Footer>
			</>
		);
	}

	private validReceiptOfMoneyAmount(amount: number): string {
		if (amount < 0) {
			return "Adj meg egy összeget!";
		}

		if (amount > MAX_AMOUNT) {
			return `Maximálisan ${formatText(MAX_AMOUNT, "ft")} adható meg!`;
		}
	}

	private validInternetTransferAmount(amount: number, values: StepFields): string {
		if (amount === 0) {
			values.internetTransferCount = 0;
		}

		if (amount < 0) {
			return "Adj meg egy összeget!";
		}

		if (amount > MAX_AMOUNT) {
			return `Maximálisan ${formatText(MAX_AMOUNT, "ft")} adható meg!`;
		}
	}

	private validCashWithdrawalOwnAtmAmount(amount: number, values: StepFields): string {
		if (amount === 0 && values.cashWithdrawalOwnAtmCount !== 0) {
			return "Ha a készpénzfelvételek összege 0 Ft, akkor számuknak is nullának kell lennie.";
		}

		if (amount < 0) {
			return "Adj meg egy összeget!";
		}

		if (amount > MAX_AMOUNT) {
			return `Maximálisan ${formatText(MAX_AMOUNT, "ft")} adható meg!`;
		}
	}

	private validInternetTransferCount(count: number, values: StepFields): string {
		if (count === 0 && values.internetTransferAmount !== 0) {
			return "Ha a netbankos utalások száma 0, akkor az összegnek is nullának kell lennie.";
		}

		if (count < 0 || isNaN(count)) {
			return "Adj meg egy darabszámot!";
		}

		if (count > MAX_COUNT) {
			return `Maximálisan ${MAX_COUNT} db adható meg!`;
		}
	}

	private validCashWithdrawalOwnAtmCount(count: number, values: StepFields): string {
		if (count === 0 && values.cashWithdrawalOwnAtmAmount !== 0) {
			return "Ha a készpénzfelvételek száma 0, akkor az összegnek is nullának kell lennie.";
		}

		if (count < 0 || isNaN(count)) {
			return "Adj meg egy darabszámot!";
		}

		if (count > MAX_COUNT) {
			return `Maximálisan ${MAX_COUNT} db adható meg!`;
		}
	}

	protected getComponentName(): string {
		return "Step1";
	}
}
